import React, { useState, useEffect } from 'react';

export default function Loading({ user }) {
  const [progress, setProgress] = useState(0);
  const [currentPhase, setCurrentPhase] = useState(0);
  const [particles, setParticles] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const darkMode = true; // You can toggle this

  const phases = [
    "Initializing Neural Networks",
    "Loading AI Models", 
    "Calibrating Algorithms",
    "Optimizing Performance",
    "Preparing Interface",
    "Welcome to ABILOOP"
  ];

  // Generate particles for background animation
  useEffect(() => {
    const generateParticles = () => {
      const newParticles = [];
      for (let i = 0; i < 50; i++) {
        newParticles.push({
          id: i,
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 4 + 1,
          opacity: Math.random() * 0.6 + 0.2,
          speed: Math.random() * 2 + 0.5,
          direction: Math.random() * Math.PI * 2
        });
      }
      setParticles(newParticles);
    };

    generateParticles();

    // Animate particles
    const particleInterval = setInterval(() => {
      setParticles(prev => prev.map(particle => ({
        ...particle,
        x: (particle.x + Math.cos(particle.direction) * particle.speed) % 100,
        y: (particle.y + Math.sin(particle.direction) * particle.speed) % 100,
        opacity: 0.2 + (Math.sin(Date.now() * 0.001 + particle.id) + 1) * 0.3
      })));
    }, 50);

    return () => clearInterval(particleInterval);
  }, []);

  // Progress simulation
  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + Math.random() * 3;
        if (newProgress >= 100) {
          setIsComplete(true);
          clearInterval(progressInterval);
          return 100;
        }
        return newProgress;
      });
    }, 100);

    return () => clearInterval(progressInterval);
  }, []);

  // Phase progression
  useEffect(() => {
    const phaseInterval = setInterval(() => {
      setCurrentPhase(prev => {
        if (progress > (prev + 1) * 16.67 && prev < phases.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, 200);

    return () => clearInterval(phaseInterval);
  }, [progress, phases.length]);

  const CircularProgress = ({ progress, size = 200 }) => {
    const radius = (size - 20) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    return (
      <div className="relative" style={{ width: size, height: size }}>
        <svg className="transform -rotate-90" width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={darkMode ? '#1e293b' : '#e2e8f0'}
            strokeWidth="2"
            fill="none"
            className="opacity-30"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="url(#gradient)"
            strokeWidth="3"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-300 ease-out"
            style={{
              filter: 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.5))'
            }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className={`text-3xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
            {Math.round(progress)}%
          </div>
        </div>
        <defs>
          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="50%" stopColor="#8b5cf6" />
            <stop offset="100%" stopColor="#06b6d4" />
          </linearGradient>
        </defs>
      </div>
    );
  };

  const WaveAnimation = () => (
    <div className="absolute bottom-0 left-0 right-0 overflow-hidden h-32">
      <svg className="absolute bottom-0 left-0 w-full h-full" viewBox="0 0 1200 200">
        <defs>
          <linearGradient id="waveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={darkMode ? '#1e40af' : '#3b82f6'} stopOpacity="0.8" />
            <stop offset="100%" stopColor={darkMode ? '#1e3a8a' : '#1d4ed8'} stopOpacity="0.2" />
          </linearGradient>
        </defs>
        <path
          d="M0,160 C300,200 600,120 1200,160 L1200,200 L0,200 Z"
          fill="url(#waveGradient)"
          className="animate-pulse"
        />
        <path
          d="M0,180 C300,140 600,200 1200,180 L1200,200 L0,200 Z"
          fill={darkMode ? '#1e3a8a' : '#2563eb'}
          fillOpacity="0.4"
          className="animate-pulse"
          style={{ animationDelay: '1s' }}
        />
      </svg>
    </div>
  );

  const DataStream = () => (
    <div className="absolute right-8 top-1/2 transform -translate-y-1/2 space-y-2">
      {[...Array(8)].map((_, i) => (
        <div
          key={i}
          className={`h-1 bg-gradient-to-r from-transparent via-blue-400 to-transparent rounded-full animate-pulse`}
          style={{
            width: `${Math.random() * 100 + 50}px`,
            animationDelay: `${i * 0.2}s`,
            animationDuration: '2s'
          }}
        />
      ))}
    </div>
  );

  const GeometricShapes = () => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className={`absolute top-20 left-20 w-16 h-16 border-2 ${darkMode ? 'border-blue-400' : 'border-blue-600'} rotate-45 animate-spin`} style={{ animationDuration: '8s' }} />
      <div className={`absolute bottom-20 right-20 w-12 h-12 ${darkMode ? 'bg-purple-500' : 'bg-purple-600'} rounded-full animate-bounce`} style={{ animationDelay: '1s' }} />
      <div className={`absolute top-1/3 right-1/3 w-8 h-8 ${darkMode ? 'bg-emerald-400' : 'bg-emerald-500'} transform rotate-45 animate-pulse`} />
    </div>
  );

  if (isComplete) {
    return (
      <div className={`fixed inset-0 flex items-center justify-center transition-all duration-1000 ${darkMode ? 'bg-slate-900' : 'bg-blue-50'}`}>
        <div className="text-center animate-fade-in">
          <div className={`text-8xl font-light mb-8 ${darkMode ? 'text-blue-400' : 'text-blue-600'} animate-pulse`}>
            ABILOOP
          </div>
          <div className={`text-2xl mb-4 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
            ✓ Ready
          </div>
          <div className={`text-xl ${darkMode ? 'text-blue-200' : 'text-blue-700'}`}>
              {user ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: `Welcome back, <span class="font-semibold text-white">${user.username}</span>`,
                  }}
                />
              ) : (
                'Loading your personalized experience...'
              )}
            </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed inset-0 transition-all duration-1000 ${darkMode ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'}`}>
      
      {/* Animated Background Particles */}
      <div className="absolute inset-0 overflow-hidden">
        {particles.map(particle => (
          <div
            key={particle.id}
            className={`absolute w-1 h-1 ${darkMode ? 'bg-blue-400' : 'bg-blue-600'} rounded-full`}
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              opacity: particle.opacity,
              transform: `scale(${particle.size})`,
              boxShadow: `0 0 ${particle.size * 2}px rgba(59, 130, 246, 0.5)`
            }}
          />
        ))}
      </div>

      {/* Geometric Background Elements */}
      <GeometricShapes />

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen">
        <div className="text-center max-w-2xl mx-auto px-8">
          
          {/* Logo with Glow Effect */}
          <div className="mb-12">
            <h1 className={`text-8xl font-light mb-4 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} 
                style={{ 
                  textShadow: darkMode ? '0 0 30px rgba(59, 130, 246, 0.5)' : '0 0 30px rgba(59, 130, 246, 0.3)',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  letterSpacing: '0.05em'
                }}>
              ABILOOP
            </h1>
            <div className={`h-1 w-32 mx-auto bg-gradient-to-r from-blue-400 via-purple-500 to-emerald-400 rounded-full animate-pulse`} />
          </div>

          {/* Circular Progress Indicator */}
          <div className="mb-12 flex justify-center">
            <CircularProgress progress={progress} size={220} />
          </div>

          {/* Phase Indicator */}
          <div className="mb-8">
            <div className={`text-xl mb-4 ${darkMode ? 'text-blue-300' : 'text-blue-600'} font-medium`}>
              {phases[currentPhase]}
            </div>
            <div className="flex justify-center space-x-2">
              {phases.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 w-8 rounded-full transition-all duration-500 ${
                    index <= currentPhase
                      ? 'bg-gradient-to-r from-blue-400 to-emerald-400'
                      : darkMode ? 'bg-slate-700' : 'bg-blue-200'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* User Welcome */}
            <div className={`text-lg ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              {user ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: `Welcome back, <span class="${`font-semibold ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}">${user.username}</span>`,
                  }}
                />
              ) : (
                <div className={`text-2xl mb-8 ${darkMode ? 'text-blue-200' : 'text-blue-700'} font-light`}>
                  Preparing your personalized experience ....
                </div>
              )}
            </div>
       

          {/* Technical Details */}
          <div className={`mt-12 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'} font-mono`}>
            <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
              <div>Neural Networks: Active</div>
              <div>AI Models: {Math.min(currentPhase + 1, 6)}/6</div>
              <div>Memory: {Math.round(progress * 2.56)}MB</div>
              <div>Latency: {Math.round(20 - progress * 0.15)}ms</div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Stream Animation */}
      <DataStream />

      {/* Wave Animation at Bottom */}
      <WaveAnimation />

      {/* Gradient Overlays */}
      <div className={`absolute inset-0 bg-gradient-to-t ${darkMode ? 'from-transparent via-transparent to-slate-900/20' : 'from-transparent via-transparent to-blue-50/20'} pointer-events-none`} />
      
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }
        
        @keyframes loading-bar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .animate-loading-bar {
          animation: loading-bar infinite linear;
        }
      `}</style>
    </div>
  );
};
